import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BdsTableCell, BdsTableRow, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import { useAiTokenConsumptionContext } from '../../../../contexts/Billing/AiTokenConsumptionContext';
import { useAiTokenConsumptionDataContext } from '../../../../contexts/Billing/AiTokenConsumptionDataContext';
import TableWithPagination from '../../../../components/TableWithPagination/TableWithPagination';
import { usePaginatedSearch } from '../../../../hooks/usePaginatedSearch';
import { formatDate } from '../../../../utils/date';
import { sortByProperty } from '../../../../utils/array';
import { Order } from '../../../../models/revenue/Order';
import { AiTokenConsumptionPaper, DailyTitleContainer } from '../styles';

const PAGE_OPTIONS = [5, 10, 15];
const COLUMN_KEYS = ['dateReference', 'aiToken'];

const AiTokenConsumptionDailyUsage: React.FC = () => {
  const { content } = useAiTokenConsumptionContext();
  const { dailyAiTokenConsumption, isAiTokenLoading } = useAiTokenConsumptionDataContext();

  const {
    page,
    itemsPerPage,
    handlePaginationChange,
    handleItemsPerPageChange,
    resetToFirstPage,
  } = usePaginatedSearch({
    pageOptions: PAGE_OPTIONS,
  });

  const [sortConfig, setSortConfig] = useState({ column: '', order: Order.Ascending });

  const handleSortChange = useCallback((column: string) => {
    setSortConfig((prev) => ({
      column,
      order: prev.column === column && prev.order === Order.Ascending ? Order.Descending : Order.Ascending,
    }));
    resetToFirstPage();
  }, [resetToFirstPage]);

  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    resetToFirstPage();
  }, [dailyAiTokenConsumption, resetToFirstPage]);

  const sortedData = useMemo(
    () => sortByProperty([...dailyAiTokenConsumption], sortConfig.column, sortConfig.order),
    [dailyAiTokenConsumption, sortConfig],
  );

  const paginatedData = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedData.slice(start, start + itemsPerPage);
  }, [sortedData, page, itemsPerPage]);

  const tableRows = useMemo(
    () =>
      paginatedData.length > 0
        ? paginatedData.map((item) => (
            <BdsTableRow key={item.dateReference}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {formatDate(new Date(item.dateReference), content.locale)}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.aiToken.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedData, content.locale],
  );

  return (
    <AiTokenConsumptionPaper>
      <DailyTitleContainer>
        <BdsTypo variant="fs-24" bold="bold" margin={false}>
          {content.dailyConsumption.title}
        </BdsTypo>
        <BdsTypo variant="fs-14" margin={false}>
          {content.dailyConsumption.description}
        </BdsTypo>
      </DailyTitleContainer>
      <TableWithPagination
        options={{
          heading: [content.dailyConsumption.columnDate, content.dailyConsumption.columnTotalTokens],
          columnKeys: COLUMN_KEYS,
          sortConfig,
          onSortChange: handleSortChange,
          itemsPerPageList: PAGE_OPTIONS,
          itemsPerPage,
          numberOfItems: isAiTokenLoading ? itemsPerPage : sortedData.length,
          isLoading: isAiTokenLoading,
          hasEmptyState: !isAiTokenLoading && sortedData.length === 0,
          emptyStateSubtitle: '',
          columnWidths: ['50%', '50%'],
          backgroundColor: '#f6f6f6',
          isAutoHeight: true,
        }}
        onPaginationChange={handlePaginationChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      >
        {tableRows}
      </TableWithPagination>
    </AiTokenConsumptionPaper>
  );
};

export default AiTokenConsumptionDailyUsage;
